import { useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { useMemo } from "react";
import type { Appointment } from "@/domains/appointment/appointments/types/appointment";
import {
  MY_TODAYS_APPOINTMENTS_QUERY,
  MY_UPCOMING_APPOINTMENTS_QUERY,
} from "@/graphql/appointments";
import { useSalonStore } from "@/lib/store/salon";
import { timezoneUtils } from "@/lib/timezone";
import type { WorkerAppointment, WorkerScheduleStats } from "../types";
import { useCurrentWorker } from "./use-current-worker";

const transformToWorkerAppointment = (appointment: Appointment): WorkerAppointment => ({
  id: appointment.id,
  startTime: appointment.startTime,
  endTime: appointment.endTime,
  bookingStatus: appointment.bookingStatus,
  outcome: appointment.outcome as string | null,
  customer: appointment.customer as {
    firstName?: string;
    lastName?: string;
    tier?: string | null;
  } | null,
  appointmentServices: appointment.appointmentServices as WorkerAppointment["appointmentServices"],
  totalPrice: appointment.totalPrice,
  customerMessage: appointment.customerMessage,
});

// Split an appointment into separate blocks when worker services are non-consecutive
const _splitAppointmentByConsecutiveServices = (
  appointment: Appointment,
  currentWorkerId: string
): WorkerAppointment[] => {
  const services = appointment.appointmentServices || [];
  const workerServices = services.filter((service) => service.workerId === currentWorkerId);

  if (workerServices.length === 0) {
    return [transformToWorkerAppointment(appointment)];
  }

  // Sort all services by sequence order
  const sortedServices = [...services].sort((a, b) => a.sequenceOrder - b.sequenceOrder);

  // Find consecutive groups of worker services
  const consecutiveGroups: Array<{
    services: typeof workerServices;
    startIndex: number;
    endIndex: number;
  }> = [];

  let currentGroup: typeof workerServices = [];
  let groupStartIndex = -1;
  let groupEndIndex = -1;

  sortedServices.forEach((service, index) => {
    const isWorkerService = service.workerId === currentWorkerId;

    if (isWorkerService) {
      if (currentGroup.length === 0) {
        // Start new group
        groupStartIndex = index;
      }
      currentGroup.push(service);
      groupEndIndex = index;
    } else {
      if (currentGroup.length > 0) {
        // End current group
        consecutiveGroups.push({
          services: [...currentGroup],
          startIndex: groupStartIndex,
          endIndex: groupEndIndex,
        });
        currentGroup = [];
      }
    }
  });

  // Add final group if exists
  if (currentGroup.length > 0) {
    consecutiveGroups.push({
      services: [...currentGroup],
      startIndex: groupStartIndex,
      endIndex: groupEndIndex,
    });
  }

  // Create separate appointment cards for each consecutive group
  return consecutiveGroups.map((group, groupIndex) => {
    // Calculate start time for this group
    let groupStartTime = timezoneUtils.fromISO(appointment.startTime);
    for (let i = 0; i < group.startIndex; i++) {
      groupStartTime = groupStartTime.plus({
        minutes: sortedServices[i].serviceDuration,
      });
    }

    // Calculate end time for this group
    const groupDuration = group.services.reduce(
      (total, service) => total + service.serviceDuration,
      0
    );
    const groupEndTime = groupStartTime.plus({ minutes: groupDuration });

    // Calculate price for this group
    const groupPrice = group.services.reduce(
      (total, service) => total + (service.totalPrice || 0),
      0
    );

    return {
      id: `${appointment.id}-block-${groupIndex}`,
      originalId: appointment.id,
      startTime: groupStartTime.toISO() ?? groupStartTime.toISODate() ?? "",
      endTime: groupEndTime.toISO() ?? groupEndTime.toISODate() ?? "",
      bookingStatus: appointment.bookingStatus,
      outcome: appointment.outcome as string | null,
      customer: appointment.customer as {
        firstName?: string;
        lastName?: string;
        tier?: string | null;
      } | null,
      appointmentServices: group.services as WorkerAppointment["appointmentServices"],
      totalPrice: groupPrice,
      customerMessage: appointment.customerMessage,
      isConsecutiveBlock: true,
      blockIndex: groupIndex,
      totalBlocks: consecutiveGroups.length,
    };
  });
};

/**
 * Hook for getting worker-specific today's appointments - only appointments assigned to the current worker
 */
export const useWorkerTodayAppointments = () => {
  const { currentWorker } = useCurrentWorker();
  const { currentSalon } = useSalonStore();

  const { data, loading, error, refetch } = useQuery(MY_TODAYS_APPOINTMENTS_QUERY, {
    variables: {
      salonId: currentSalon?.id ?? "",
      filters: {
        first: 50, // Get more appointments for today
        sortOrder: "asc",
      },
    },
    skip: !currentWorker || !currentSalon?.id,
    fetchPolicy: "cache-and-network",
  });

  const workerAppointments = useMemo(() => {
    if (!data?.myTodaysAppointments?.edges || !currentWorker) return [];

    // Transform appointments and filter out those without worker services
    return data.myTodaysAppointments.edges
      .map((edge) => transformToWorkerAppointment(edge.node))
      .filter((appointment) => {
        const services = appointment.appointmentServices || [];
        return services.some((service) => service.workerId === currentWorker.id);
      });
  }, [data, currentWorker]);

  return {
    appointments: workerAppointments,
    loading,
    error,
    hasMore: data?.myTodaysAppointments?.pageInfo?.hasNextPage || false,
    loadMore: () => {}, // TODO: Implement pagination
    retry: refetch,
  };
};

/**
 * Hook for getting worker's upcoming appointments
 */
export const useWorkerUpcomingAppointments = () => {
  const { currentWorker } = useCurrentWorker();
  const { currentSalon } = useSalonStore();

  const { data, loading, error, refetch } = useQuery(MY_UPCOMING_APPOINTMENTS_QUERY, {
    variables: {
      salonId: currentSalon?.id ?? "",
      filters: {
        first: 20,
        sortOrder: "asc",
      },
    },
    skip: !currentWorker || !currentSalon?.id,
    fetchPolicy: "cache-and-network",
  });

  const workerAppointments = useMemo(() => {
    if (!data?.myUpcomingAppointments?.edges || !currentWorker) return [];

    // Transform appointments and filter out those without worker services
    return data.myUpcomingAppointments.edges
      .map((edge) => transformToWorkerAppointment(edge.node))
      .filter((appointment) => {
        const services = appointment.appointmentServices || [];
        return services.some((service) => service.workerId === currentWorker.id);
      });
  }, [data, currentWorker]);

  return {
    appointments: workerAppointments,
    loading,
    error,
    hasMore: data?.myUpcomingAppointments?.pageInfo?.hasNextPage || false,
    loadMore: () => {}, // TODO: Implement pagination
    retry: refetch,
  };
};

/**
 * Hook for getting worker's completed appointments
 * For now, we'll filter from today's and upcoming appointments
 * In the future, this could use a dedicated history query
 */
export const useWorkerCompletedAppointments = () => {
  const { appointments: todayAppointments } = useWorkerTodayAppointments();
  const { appointments: upcomingAppointments } = useWorkerUpcomingAppointments();

  const completedAppointments = useMemo(() => {
    // Combine today's and upcoming appointments, then filter for completed ones
    const allAppointments = [...todayAppointments, ...upcomingAppointments];
    return allAppointments.filter((apt) => apt.outcome === "COMPLETED");
  }, [todayAppointments, upcomingAppointments]);

  return {
    appointments: completedAppointments,
    loading: false,
    error: null,
    hasMore: false,
    loadMore: () => {},
    retry: () => {},
  };
};

/**
 * Hook for getting worker schedule statistics and current/next appointment info
 */
export const useWorkerScheduleStats = (): WorkerScheduleStats => {
  const { appointments: todayAppointments } = useWorkerTodayAppointments();
  const { appointments: upcomingAppointments } = useWorkerUpcomingAppointments();

  return useMemo(() => {
    const now = DateTime.fromJSDate(new Date());

    // Filter today's appointments
    const confirmedToday = todayAppointments.filter((apt) => apt.bookingStatus === "CONFIRMED");

    const completed = confirmedToday.filter((apt) => apt.outcome === "COMPLETED");

    // Find current appointment (in progress)
    const currentAppointment = confirmedToday.find((apt) => {
      const startTime = timezoneUtils.fromISO(apt.startTime);
      const endTime = timezoneUtils.fromISO(apt.endTime);
      return now >= startTime && now <= endTime && apt.outcome === "IN_PROGRESS";
    });

    // Find next appointment (upcoming today)
    const upcomingToday = confirmedToday
      .filter((apt) => {
        const startTime = timezoneUtils.fromISO(apt.startTime);
        return startTime > now && !apt.outcome;
      })
      .sort((a, b) => {
        const aTime = timezoneUtils.fromISO(a.startTime);
        const bTime = timezoneUtils.fromISO(b.startTime);
        return aTime.toMillis() - bTime.toMillis();
      });

    const nextAppointment = upcomingToday[0] || null;

    return {
      totalAppointments: confirmedToday.length,
      completedAppointments: completed.length,
      upcomingAppointments: upcomingAppointments.length,
      currentAppointment,
      nextAppointment,
    };
  }, [todayAppointments, upcomingAppointments]);
};
